(() => {
  if (window.depotPredictiveSearchInitialized) return;
  window.depotPredictiveSearchInitialized = true;

  const selector = '[data-predictive-search]';
  const responseSectionId = 'shopify-section-predictive-search';
  const inputDelay = 120;
  const recentLimit = 6;
  const queryLimit = 120;
  const validScopes = new Set(['all', 'product', 'article', 'page']);
  const locale = (document.documentElement.lang || 'default').toLowerCase();
  const recentStorageKey = `depot:recent-searches:${locale}:v1`;
  const cache = new Map();

  let instanceCount = 0;
  let memoryRecentSearches = [];

  const normalizeQuery = (value) => String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, queryLimit);

  const normalizeScope = (value) => validScopes.has(value) ? value : 'all';

  const sanitizeRecentSearches = (value) => {
    if (!Array.isArray(value)) return [];

    const seen = new Set();
    return value.reduce((records, item) => {
      const query = normalizeQuery(typeof item === 'string' ? item : item?.query);
      const scope = normalizeScope(typeof item === 'string' ? 'all' : item?.scope);
      const identity = query.toLocaleLowerCase();
      if (!query || seen.has(identity) || records.length >= recentLimit) return records;
      seen.add(identity);
      records.push({ query, scope });
      return records;
    }, []);
  };

  const readRecentSearches = () => {
    try {
      const stored = window.localStorage.getItem(recentStorageKey);
      memoryRecentSearches = stored === null ? [] : sanitizeRecentSearches(JSON.parse(stored));
    } catch (_error) {
      // Storage can be unavailable in private or restricted browsing contexts.
    }
    return memoryRecentSearches.slice();
  };

  const writeRecentSearches = (records) => {
    memoryRecentSearches = sanitizeRecentSearches(records);
    try {
      window.localStorage.setItem(recentStorageKey, JSON.stringify(memoryRecentSearches));
    } catch (_error) {
      // Keep the in-memory list usable when persistent storage is unavailable.
    }
  };

  const rememberSearch = (queryValue, scopeValue = 'all') => {
    const query = normalizeQuery(queryValue);
    if (!query) return;

    const identity = query.toLocaleLowerCase();
    const records = readRecentSearches().filter(record => record.query.toLocaleLowerCase() !== identity);
    records.unshift({ query, scope: normalizeScope(scopeValue) });
    writeRecentSearches(records);
  };

  const clearRecentSearches = () => {
    memoryRecentSearches = [];
    try {
      window.localStorage.removeItem(recentStorageKey);
    } catch (_error) {
      // The in-memory list has still been cleared.
    }
  };

  const rememberSubmittedSearch = (event) => {
    const form = event.target;
    if (!(form instanceof Element) || !form.matches('form[role="search"]')) return;

    const queryControl = form.querySelector('[name="q"]');
    if (!queryControl || !('value' in queryControl)) return;

    const typeControl = form.querySelector('[data-predictive-search-type]');
    const scope = typeControl && !typeControl.disabled ? typeControl.value : 'all';
    rememberSearch(queryControl.value, scope);
  };

  document.addEventListener('submit', rememberSubmittedSearch, true);

  function initialize(root) {
    if (root.predictiveSearchCleanup) return;

    const input = root.querySelector('[data-predictive-search-input]');
    const panel = root.querySelector('[data-predictive-search-panel]');
    const results = root.querySelector('[data-predictive-search-results]');
    const status = root.querySelector('[data-predictive-search-status]');
    const message = root.querySelector('[data-predictive-search-message]');
    const recentHeader = root.querySelector('[data-predictive-search-recent-header]');
    const clearRecentButton = root.querySelector('[data-predictive-search-clear-recent]');
    const scopeGroup = root.querySelector('[data-predictive-search-scopes]');
    const scopeButtons = [...root.querySelectorAll('[data-predictive-search-scope]')];
    const typeControl = root.querySelector('[data-predictive-search-type]');
    const endpoint = root.dataset.predictiveSearchUrl;
    const supported = (
      typeof window.fetch === 'function'
      && typeof window.AbortController === 'function'
      && typeof window.DOMParser === 'function'
      && typeof window.URL === 'function'
    );

    if (
      !input
      || !panel
      || !results
      || !status
      || !message
      || !recentHeader
      || !clearRecentButton
      || !scopeGroup
      || !scopeButtons.length
      || !typeControl
      || !endpoint
      || !supported
    ) return;

    const instanceId = `PredictiveSearch-${++instanceCount}`;
    let activeScope = scopeButtons.find(button => button.getAttribute('aria-checked') === 'true') || scopeButtons[0];
    let revision = 0;
    let debounceTimer = 0;
    let focusoutTimer = 0;
    let controller = null;
    let scheduledKey = '';
    let requestKey = '';
    let renderedKey = '';
    let view = 'idle';
    let isComposing = false;
    let suppressNextFocusOpen = false;
    let destroyed = false;

    const currentQuery = () => normalizeQuery(input.value);
    const currentScope = () => normalizeScope(activeScope?.dataset.predictiveSearchScope);
    const currentKey = () => `${currentScope()}\u001f${currentQuery()}`;
    const cacheKey = key => `${endpoint}\u001f${key}`;
    const options = () => [...results.querySelectorAll('[data-predictive-search-option][role="option"][id]')];
    const selectedOption = () => results.querySelector('[data-predictive-search-option][aria-selected="true"]');
    const containsFocus = () => root.contains(document.activeElement);

    const setStatus = (value = '') => {
      status.textContent = value;
    };

    const setMessage = (value = '') => {
      message.textContent = value;
      message.hidden = !value;
    };

    const clearSelection = () => {
      options().forEach(option => option.setAttribute('aria-selected', 'false'));
      input.removeAttribute('aria-activedescendant');
    };

    const close = () => {
      clearSelection();
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
    };

    const focusInputWithoutOpening = () => {
      if (document.activeElement === input) return;
      suppressNextFocusOpen = true;
      input.focus({ preventScroll: true });
      queueMicrotask(() => {
        suppressNextFocusOpen = false;
      });
    };

    const open = () => {
      if (destroyed || !root.isConnected) return false;
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      return true;
    };

    const hideRecentHeader = () => {
      recentHeader.hidden = true;
      clearRecentButton.hidden = true;
    };

    const cancelPending = ({ closePanel = true, clearResults = false, clearStatus = true } = {}) => {
      revision += 1;
      window.clearTimeout(debounceTimer);
      debounceTimer = 0;
      scheduledKey = '';
      controller?.abort();
      controller = null;
      requestKey = '';
      input.removeAttribute('aria-busy');

      if (clearResults) {
        clearSelection();
        results.replaceChildren();
        renderedKey = '';
        view = 'idle';
        hideRecentHeader();
        setMessage();
      }
      if (closePanel) close();
      if (clearStatus) setStatus();
    };

    const requestIsCurrent = (requestRevision, key) => (
      !destroyed
      && root.isConnected
      && revision === requestRevision
      && currentKey() === key
    );

    const namespaceResponseIds = () => {
      const idMap = new Map();
      results.querySelectorAll('[id]').forEach((element, index) => {
        const previousId = element.id;
        const safeId = previousId.replace(/[^a-zA-Z0-9_-]/g, '-');
        const nextId = `${instanceId}-${safeId}-${index}`;
        idMap.set(previousId, nextId);
        element.id = nextId;
      });

      ['aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns'].forEach((attribute) => {
        results.querySelectorAll(`[${attribute}]`).forEach((element) => {
          const references = element.getAttribute(attribute).split(/\s+/).map(reference => idMap.get(reference) || reference);
          element.setAttribute(attribute, references.join(' '));
        });
      });

      results.querySelectorAll('label[for]').forEach((label) => {
        const target = label.getAttribute('for');
        if (idMap.has(target)) label.setAttribute('for', idMap.get(target));
      });
    };

    const updateAllResultsLink = () => {
      const link = results.querySelector('[data-predictive-search-all-link]');
      if (!link) return;

      const url = new URL(link.getAttribute('href'), window.location.origin);
      const searchType = activeScope.dataset.predictiveSearchSearchType || '';
      if (searchType) url.searchParams.set('type', searchType);
      else url.searchParams.delete('type');
      link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
    };

    const render = (key, payload, requestRevision) => {
      if (!requestIsCurrent(requestRevision, key)) return;

      clearSelection();
      results.innerHTML = payload.markup;
      namespaceResponseIds();
      updateAllResultsLink();
      renderedKey = key;
      view = 'results';
      hideRecentHeader();
      setMessage();
      input.removeAttribute('aria-busy');
      setStatus(payload.statusText);
      if (containsFocus()) open();
    };

    const showLoading = () => {
      results.replaceChildren();
      renderedKey = '';
      view = 'loading';
      hideRecentHeader();
      setMessage(panel.dataset.predictiveSearchLoadingText || '');
      input.setAttribute('aria-busy', 'true');
      setStatus(panel.dataset.predictiveSearchLoadingText || '');
      if (containsFocus()) open();
    };

    const getSearchResults = async (query, key, requestRevision) => {
      if (!requestIsCurrent(requestRevision, key)) return;

      const stored = cache.get(cacheKey(key));
      if (stored) {
        render(key, stored, requestRevision);
        return;
      }

      const requestController = new AbortController();
      const resourceTypes = activeScope.dataset.predictiveSearchResourceTypes;
      controller = requestController;
      requestKey = key;
      input.setAttribute('aria-busy', 'true');

      try {
        const url = new URL(endpoint, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', resourceTypes);
        url.searchParams.set('resources[limit]', '10');
        url.searchParams.set('resources[limit_scope]', 'all');
        url.searchParams.set('section_id', 'predictive-search');

        const response = await fetch(`${url.pathname}${url.search}`, {
          credentials: 'same-origin',
          headers: { 'Accept': 'text/html' },
          signal: requestController.signal
        });
        if (!response.ok) throw new Error(`Predictive search request failed: ${response.status}`);

        const responseText = await response.text();
        const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const responseSection = responseDocument.getElementById(responseSectionId);
        const responseRoot = responseSection?.querySelector('[data-predictive-search-response]');
        const responseOptions = responseRoot?.querySelector('[data-predictive-search-options]');
        const responseStatus = responseRoot?.querySelector('[data-predictive-search-status-text]');
        if (!responseRoot || !responseOptions || !responseStatus) {
          throw new Error('Predictive search section response is incomplete');
        }

        const payload = {
          markup: responseOptions.innerHTML,
          statusText: responseStatus.textContent.trim()
        };
        cache.set(cacheKey(key), payload);
        render(key, payload, requestRevision);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        if (!requestIsCurrent(requestRevision, key)) return;
        const focusWasInPanel = panel.contains(document.activeElement);
        clearSelection();
        results.replaceChildren();
        renderedKey = '';
        view = 'idle';
        setMessage();
        hideRecentHeader();
        close();
        if (focusWasInPanel) focusInputWithoutOpening();
        setStatus(panel.dataset.predictiveSearchErrorText || '');
      } finally {
        if (controller === requestController && requestIsCurrent(requestRevision, key)) {
          controller = null;
          requestKey = '';
          input.removeAttribute('aria-busy');
        }
      }
    };

    const scheduleSearch = (query, delay = inputDelay) => {
      const requestRevision = revision;
      const key = currentKey();
      scheduledKey = key;
      debounceTimer = window.setTimeout(() => {
        debounceTimer = 0;
        scheduledKey = '';
        getSearchResults(query, key, requestRevision);
      }, delay);
    };

    const buildSearchUrl = (query, scope) => {
      const url = new URL(root.getAttribute('action') || window.location.pathname, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('options[prefix]', 'last');
      if (scope !== 'all') url.searchParams.set('type', scope);
      else url.searchParams.delete('type');
      return `${url.pathname}${url.search}${url.hash}`;
    };

    const renderRecentSearches = ({ announce = true } = {}) => {
      const records = readRecentSearches();
      const scopeLabels = new Map(scopeButtons.map(button => [
        normalizeScope(button.dataset.predictiveSearchScope),
        button.textContent.trim()
      ]));

      clearSelection();
      results.replaceChildren();
      records.forEach((record, index) => {
        const option = document.createElement('div');
        const link = document.createElement('a');
        const term = document.createElement('span');

        option.id = `${instanceId}-Recent-${index}`;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.dataset.predictiveSearchOption = '';

        link.className = 'predictive-search__item predictive-search__item--recent';
        link.href = buildSearchUrl(record.query, record.scope);
        link.tabIndex = -1;
        link.dataset.predictiveSearchTerm = record.query;
        link.dataset.predictiveSearchRecentScope = record.scope;

        term.className = 'predictive-search__recent-term';
        term.textContent = record.query;
        link.append(term);

        if (record.scope !== 'all') {
          const scope = document.createElement('span');
          scope.className = 'predictive-search__recent-scope';
          scope.textContent = scopeLabels.get(record.scope) || '';
          link.append(scope);
        }

        option.append(link);
        results.append(option);
      });

      recentHeader.hidden = false;
      clearRecentButton.hidden = records.length === 0;
      renderedKey = '';
      view = 'recent';
      input.removeAttribute('aria-busy');
      setMessage(records.length ? '' : panel.dataset.predictiveSearchRecentEmptyText || '');
      if (announce) setStatus(records.length ? panel.dataset.predictiveSearchRecentAvailableText || '' : '');
      if (containsFocus()) open();
    };

    const renderCachedOrSchedule = (query, delay = inputDelay) => {
      const key = currentKey();
      const stored = cache.get(cacheKey(key));
      if (stored) {
        render(key, stored, revision);
        return;
      }
      showLoading();
      scheduleSearch(query, delay);
    };

    const syncScopeControls = () => {
      scopeButtons.forEach((button) => {
        const selected = button === activeScope;
        button.setAttribute('aria-checked', String(selected));
        button.tabIndex = selected ? 0 : -1;
      });

      const searchType = activeScope.dataset.predictiveSearchSearchType || '';
      typeControl.value = searchType;
      typeControl.disabled = !searchType;
    };

    const applyScope = (button, { announce = true } = {}) => {
      if (!button || button === activeScope) return;
      activeScope = button;
      syncScopeControls();

      const query = currentQuery();
      cancelPending({ closePanel: false, clearResults: true, clearStatus: !announce });
      if (query) renderCachedOrSchedule(query, 0);
      else renderRecentSearches({ announce: false });
      if (announce && !query) setStatus(button.dataset.predictiveSearchScopeStatus || '');
    };

    const viewMatchesCurrentValue = () => {
      const query = currentQuery();
      if (!query) return view === 'recent';
      return view === 'results' && renderedKey === currentKey();
    };

    const select = (option) => {
      if (!option) return;
      options().forEach(candidate => candidate.setAttribute('aria-selected', candidate === option ? 'true' : 'false'));
      input.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView?.({ block: 'nearest' });
    };

    const move = (direction) => {
      if (!viewMatchesCurrentValue()) return false;
      const availableOptions = options();
      if (!availableOptions.length) return false;
      if (panel.hidden && !open()) return false;

      const currentIndex = availableOptions.indexOf(selectedOption());
      const nextIndex = currentIndex === -1
        ? (direction > 0 ? 0 : availableOptions.length - 1)
        : Math.max(0, Math.min(availableOptions.length - 1, currentIndex + direction));
      select(availableOptions[nextIndex]);
      return true;
    };

    const activateSelectedOption = () => {
      if (panel.hidden || !viewMatchesCurrentValue()) return false;
      const link = selectedOption()?.querySelector('a[href]');
      if (!link) return false;
      link.click();
      return true;
    };

    const handleValueChange = () => {
      const query = currentQuery();
      const key = currentKey();

      if (!query) {
        cancelPending({ closePanel: false, clearResults: true });
        renderRecentSearches();
        return;
      }

      if (key === renderedKey || key === scheduledKey || key === requestKey) {
        if (containsFocus()) open();
        return;
      }

      cancelPending({ closePanel: false, clearResults: true });
      renderCachedOrSchedule(query);
    };

    const onInput = (event) => {
      if (isComposing || event.isComposing) return;
      handleValueChange();
    };

    const onCompositionStart = () => {
      isComposing = true;
      cancelPending();
    };

    const onCompositionEnd = () => {
      isComposing = false;
      handleValueChange();
    };

    const onFocus = () => {
      if (suppressNextFocusOpen) {
        suppressNextFocusOpen = false;
        return;
      }

      const query = currentQuery();
      const key = currentKey();

      if (!query) {
        cancelPending({ closePanel: false, clearResults: true });
        renderRecentSearches();
        return;
      }
      if (renderedKey === key && view === 'results') {
        open();
        return;
      }
      if (scheduledKey === key || requestKey === key) {
        open();
        return;
      }

      cancelPending({ closePanel: false, clearResults: true });
      renderCachedOrSchedule(query);
    };

    const onInputKeydown = (event) => {
      if (isComposing || event.isComposing || event.keyCode === 229) return;

      if (event.key === 'ArrowDown' && move(1)) {
        event.preventDefault();
        return;
      }
      if (event.key === 'ArrowUp' && move(-1)) {
        event.preventDefault();
        return;
      }
      if (event.key === 'Enter' && activateSelectedOption()) {
        event.preventDefault();
        return;
      }
      if (event.key === 'Escape' && (!panel.hidden || debounceTimer || controller)) {
        event.preventDefault();
        event.stopPropagation();
        cancelPending();
      }
    };

    const onRootKeydown = (event) => {
      if (event.defaultPrevented) return;

      const scopeButton = event.target.closest?.('[data-predictive-search-scope]');
      if (scopeButton && scopeGroup.contains(scopeButton)) {
        const currentIndex = scopeButtons.indexOf(scopeButton);
        const isRtl = getComputedStyle(scopeGroup).direction === 'rtl';
        let nextIndex = null;

        if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = scopeButtons.length - 1;
        else if (event.key === 'ArrowRight') nextIndex = currentIndex + (isRtl ? -1 : 1);
        else if (event.key === 'ArrowLeft') nextIndex = currentIndex + (isRtl ? 1 : -1);
        else if (event.key === 'ArrowDown') nextIndex = currentIndex + 1;
        else if (event.key === 'ArrowUp') nextIndex = currentIndex - 1;

        if (nextIndex !== null) {
          event.preventDefault();
          nextIndex = (nextIndex + scopeButtons.length) % scopeButtons.length;
          const nextButton = scopeButtons[nextIndex];
          nextButton.focus({ preventScroll: true });
          applyScope(nextButton);
          return;
        }
      }

      if (event.key === 'Escape' && event.target !== input && !panel.hidden) {
        event.preventDefault();
        cancelPending();
        focusInputWithoutOpening();
      }
    };

    const onRootClick = (event) => {
      const scopeButton = event.target.closest?.('[data-predictive-search-scope]');
      if (scopeButton && scopeGroup.contains(scopeButton)) {
        applyScope(scopeButton);
        return;
      }

      if (event.target.closest?.('[data-predictive-search-clear-recent]')) {
        clearRecentSearches();
        input.focus({ preventScroll: true });
        renderRecentSearches({ announce: false });
        setStatus(panel.dataset.predictiveSearchRecentClearedText || '');
        return;
      }

      const link = event.target.closest?.('[data-predictive-search-results] a[href]');
      if (!link || !results.contains(link)) return;
      const term = link.dataset.predictiveSearchTerm || currentQuery();
      const scope = link.dataset.predictiveSearchRecentScope || currentScope();
      rememberSearch(term, scope);
    };

    const onFocusout = () => {
      window.clearTimeout(focusoutTimer);
      focusoutTimer = window.setTimeout(() => {
        if (!root.contains(document.activeElement)) cancelPending();
      });
    };

    const onPointerDown = (event) => {
      if (!root.contains(event.target)) cancelPending();
    };

    const onReset = () => {
      window.setTimeout(() => {
        const focusIsWithinRoot = containsFocus();
        const query = currentQuery();
        syncScopeControls();
        cancelPending({ closePanel: !focusIsWithinRoot, clearResults: true });
        if (!focusIsWithinRoot) return;
        if (query) renderCachedOrSchedule(query, 0);
        else renderRecentSearches();
      });
    };

    const onHeaderDrawerOpen = () => cancelPending();

    const onStorage = (event) => {
      if (event.key !== recentStorageKey && event.key !== null) return;
      try {
        memoryRecentSearches = event.newValue === null
          ? []
          : sanitizeRecentSearches(JSON.parse(event.newValue));
      } catch (_error) {
        memoryRecentSearches = [];
      }
      if (currentQuery() || !containsFocus()) return;
      renderRecentSearches();
    };

    syncScopeControls();

    input.addEventListener('input', onInput);
    input.addEventListener('focus', onFocus);
    input.addEventListener('keydown', onInputKeydown);
    input.addEventListener('compositionstart', onCompositionStart);
    input.addEventListener('compositionend', onCompositionEnd);
    root.addEventListener('click', onRootClick);
    root.addEventListener('keydown', onRootKeydown);
    root.addEventListener('reset', onReset);
    root.addEventListener('focusout', onFocusout);
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('depot:header-drawer-open', onHeaderDrawerOpen);
    window.addEventListener('storage', onStorage);

    root.predictiveSearchClose = onHeaderDrawerOpen;
    root.predictiveSearchCleanup = () => {
      destroyed = true;
      suppressNextFocusOpen = false;
      cancelPending({ clearResults: true });
      window.clearTimeout(focusoutTimer);
      input.removeEventListener('input', onInput);
      input.removeEventListener('focus', onFocus);
      input.removeEventListener('keydown', onInputKeydown);
      input.removeEventListener('compositionstart', onCompositionStart);
      input.removeEventListener('compositionend', onCompositionEnd);
      root.removeEventListener('click', onRootClick);
      root.removeEventListener('keydown', onRootKeydown);
      root.removeEventListener('reset', onReset);
      root.removeEventListener('focusout', onFocusout);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('depot:header-drawer-open', onHeaderDrawerOpen);
      window.removeEventListener('storage', onStorage);
      delete root.predictiveSearchClose;
      delete root.predictiveSearchCleanup;
    };
  }

  function initializeWithin(scope) {
    if (scope.matches?.(selector)) initialize(scope);
    scope.querySelectorAll(selector).forEach(initialize);
  }

  function cleanupWithin(scope) {
    if (scope.matches?.(selector)) scope.predictiveSearchCleanup?.();
    scope.querySelectorAll(selector).forEach(root => root.predictiveSearchCleanup?.());
  }

  initializeWithin(document);
  document.addEventListener('shopify:section:load', event => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', event => cleanupWithin(event.target));
})();
